import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from "react";
import {
  EnrichedTask,
  Task,
  TaskStatus,
  UserStats,
} from "@src/interfaces/Rewards";
import { usePrivySmartWallet } from "./PrivySmartWalletProvider";
import {
  claimTasks,
  getPoints,
  getReferralCode,
  getTasks,
  verifyTasks,
} from "@src/services";

export interface LoadingState {
  tasks: boolean;
  points: boolean;
  referralCode: boolean;
}

export interface RewardsState {
  tasks: EnrichedTask[];
  points: {
    stats: UserStats;
    prevStats: UserStats;
  };
  referralCode: string;
  loadingState: LoadingState;
  fetchTasks: (stealthMode: boolean) => Promise<void>;
  fetchPoints: (stealthMode: boolean) => Promise<void>;
  fetchReferralCode: (stealthMode: boolean) => Promise<void>;
  fetchAll: (stealthMode: boolean) => Promise<void>;
  claimTask: (taskId: string) => void;
  verifyTask: (taskId: string) => Promise<void>;
}

const RewardsContext = createContext<RewardsState>({} as RewardsState);

interface RewardsProviderProps {
  chainIds?: string | string[];
  children: ReactNode;
}

const getTaskStatus = (task: Task, allTasks: Task[]): TaskStatus => {
  if (task.claimed) return TaskStatus.Claimed;
  if (task.completed) return TaskStatus.Completed;

  const mandatoryTasks = allTasks.filter((t) => t.isMandatory);

  // no mandatory tasks – everything unlocked
  if (mandatoryTasks.length === 0) {
    return TaskStatus.Unlocked;
  }

  // mandatory always unlocked
  if (task.isMandatory) return TaskStatus.Unlocked;

  // non-mandatory - unlocked after completing all mandatory
  const allMandatoryCompleted = mandatoryTasks.every((t) => t.completed);
  return allMandatoryCompleted ? TaskStatus.Unlocked : TaskStatus.Locked;
};

const enrichAndSortTasks = (tasks: Task[]): EnrichedTask[] => {
  const statusOrder: Record<TaskStatus, number> = {
    [TaskStatus.Completed]: 0,
    [TaskStatus.Unlocked]: 1,
    [TaskStatus.Claimed]: 2,
    [TaskStatus.Locked]: 3,
  };
  return tasks
    .map((task) => ({
      ...task,
      status: getTaskStatus(task, tasks),
    }))
    .sort((a, b) => statusOrder[a.status] - statusOrder[b.status]);
};

export const RewardsProvider = ({ children }: RewardsProviderProps) => {
  const { smartWalletAddress } = usePrivySmartWallet();
  const [tasks, setTasks] = useState<RewardsState["tasks"]>([]);
  const [referralCode, setReferralCode] =
    useState<RewardsState["referralCode"]>("");
  const [points, setPoints] = useState<RewardsState["points"]>({
    stats: {
      points: 0,
      volumePoints: 0,
      taskPoints: 0,
      referralPoints: 0,
      volume: 0,
      tasksClaimed: 0,
      referrals: 0,
    },
    prevStats: {
      points: 0,
      volumePoints: 0,
      taskPoints: 0,
      referralPoints: 0,
      volume: 0,
      tasksClaimed: 0,
      referrals: 0,
    },
  });
  const [loadingState, setLoadingState] = useState<LoadingState>({
    tasks: false,
    points: false,
    referralCode: false,
  });

  const fetchTasks = useCallback(
    async (stealthMode: boolean) => {
      if (!smartWalletAddress) return;
      if (!stealthMode) {
        setLoadingState((prev) => ({ ...prev, tasks: true }));
      }
      try {
        const rawTasks = await getTasks(smartWalletAddress);

        const enriched = enrichAndSortTasks(rawTasks);

        setTasks(enriched);

        // Only verify tasks that are unlocked, not completed, and have validationType defined
        const toVerify = enriched.filter(
          (t) =>
            t.status === TaskStatus.Unlocked &&
            !t.completed &&
            t.validationType,
        );

        if (toVerify.length > 0) {
          try {
            await verifyTasks(
              smartWalletAddress,
              toVerify.map((t) => t.taskId),
            );
            const reFetched = await getTasks(smartWalletAddress);
            setTasks(enrichAndSortTasks(reFetched));
          } catch (err) {
            console.error("Failed to verify tasks", err);
          }
        }
      } catch (e) {
        console.error("Failed to fetch tasks", e);
      } finally {
        setLoadingState((prev) => ({ ...prev, tasks: false }));
      }
    },
    [smartWalletAddress],
  );

  // Add a function to verify a single task by taskId
  const verifyTask = useCallback(
    async (taskId: string) => {
      if (!smartWalletAddress) return;
      try {
        await verifyTasks(smartWalletAddress, [taskId]);
        // Refresh tasks after verification
        const reFetched = await getTasks(smartWalletAddress);
        setTasks(enrichAndSortTasks(reFetched));
      } catch (err) {
        console.error("Failed to verify task", err);
      }
    },
    [smartWalletAddress],
  );

  const fetchPoints = useCallback(
    async (stealthMode: boolean) => {
      if (!smartWalletAddress) return;
      if (!stealthMode) {
        setLoadingState((prev) => ({ ...prev, points: true }));
      }
      try {
        const points = await getPoints(smartWalletAddress);

        setPoints(points);
      } catch (e) {
        console.error("Failed to fetch points", e);
      } finally {
        setLoadingState((prev) => ({ ...prev, points: false }));
      }
    },
    [smartWalletAddress],
  );

  const fetchReferralCode = useCallback(
    async (stealthMode: boolean) => {
      if (!smartWalletAddress) return;
      if (!stealthMode) {
        setLoadingState((prev) => ({ ...prev, referralCode: true }));
      }
      try {
        const res = await getReferralCode(smartWalletAddress);

        setReferralCode(res.refCode);
      } catch (e) {
        console.error("Failed to fetch referral code", e);
      } finally {
        setLoadingState((prev) => ({ ...prev, referralCode: false }));
      }
    },
    [smartWalletAddress],
  );

  const claimTask = useCallback(
    (taskId: string) => {
      const task = tasks.find((t) => t.taskId === taskId);
      if (task?.claimed || task?.justClaimed || !task || !smartWalletAddress)
        return;

      setTasks((prev) =>
        prev.map((task) =>
          task.taskId === taskId ? { ...task, justClaimed: true } : task,
        ),
      );

      setTimeout(() => {
        setTasks((prev) => {
          const claimedList = prev.map((task) =>
            task.taskId === taskId
              ? { ...task, claimed: true, justClaimed: false }
              : task,
          );

          const enriched = enrichAndSortTasks(claimedList);

          return enriched;
        });

        setPoints((prev) => ({
          stats: {
            ...prev.stats,
            points: prev.stats.points + task.points,
            taskPoints: prev.stats.taskPoints + task.points,
            tasksClaimed: prev.stats.tasksClaimed + 1,
          },
          prevStats: {
            ...prev.prevStats,
            points: prev.stats.points,
            taskPoints: prev.stats.taskPoints,
            tasksClaimed: prev.stats.tasksClaimed,
          },
        }));
      }, 300);

      claimTasks(smartWalletAddress, [taskId]).catch((err) =>
        console.error("Claim failed on backend", err),
      );
    },
    [smartWalletAddress, tasks],
  );

  const fetchAll = useCallback(
    async (stealthMode: boolean) => {
      try {
        // this solves the issue with new users since fetchPoints creates a new user, could be improved in the future
        await fetchPoints(stealthMode);
        await Promise.all([
          fetchTasks(stealthMode),
          fetchReferralCode(stealthMode),
        ]);
      } catch (e) {
        console.error("Failed to fetch rewards data", e);
      }
    },
    [fetchPoints, fetchTasks, fetchReferralCode],
  );

  useEffect(() => {
    fetchAll(false);
  }, [fetchAll]);

  const state = useMemo<RewardsState>(
    () => ({
      tasks,
      points,
      referralCode,
      loadingState,
      fetchTasks,
      fetchPoints,
      fetchReferralCode,
      fetchAll,
      claimTask,
      verifyTask,
    }),
    [
      tasks,
      points,
      referralCode,
      loadingState,
      fetchTasks,
      fetchPoints,
      fetchReferralCode,
      fetchAll,
      claimTask,
      verifyTask,
    ],
  );

  return (
    <RewardsContext.Provider value={state}>{children}</RewardsContext.Provider>
  );
};

export function useRewards(): RewardsState {
  return useContext(RewardsContext);
}
